import { useAuth0 } from "@auth0/auth0-react";
import { PropTypes } from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, ThumbnailPreview } from "@/atoms";
import { actionsData, fileData, permissionsData } from "@/data";
import { useEditVideo, useUploadFile } from "@/hooks";
import { canDo, getVideoThumbnailHandler } from "@/utils";

const ReleaseVideoThumbnail = ({ image, imageId, rowId, thumbnail, thumbnailId, trackId, videoId }) => {
    const { t } = useTranslation();

    const { user } = useAuth0();

    const [modalData, setModalData] = useState({ open: false });

    const { filesLoading, uploadFile } = useUploadFile();

    const { editVideoThumbnail, loading: editVideoThumbnailLoading } = useEditVideo(trackId);

    const { activeThumbnail, isCustom } = getVideoThumbnailHandler({ image, imageId, thumbnail, thumbnailId });

    const { videoThumbnail: videoThumbnailFileName } = fileData.names;

    const { mediaCreate: mediaCreatePermission, mediaEdit: mediaEditPermission } = permissionsData.permissions.keys;

    // an uploaded thumbnail is a new image (media:create) attached to the video (media:edit)
    const canUpload = !!canDo(user, [mediaCreatePermission, mediaEditPermission]);

    const canEdit = !!canDo(user, [mediaEditPermission]);

    const uploadThumbnailHandler = (file) =>
        uploadFile(file, null, videoThumbnailFileName, null, {
            onSuccess: (uploadedImageId) => editVideoThumbnail(videoId, uploadedImageId),
        });

    // reverting points the video back at its auto-generated frame, or clears it when there is none
    const deleteCustomThumbnailHandler = () =>
        editVideoThumbnail(videoId, imageId ?? null, {
            successMessage: t("messages.deleteMsgs.success", { entity: t("videos.customThumbnail") }),
        });

    return (
        <>
            <ThumbnailPreview
                accept={fileData.acceptedExtensions.videoThumbnail}
                canDelete={isCustom && canEdit}
                canUpload={canUpload}
                downloadLink={activeThumbnail?.downloadUri}
                id={videoThumbnailFileName}
                loading={!!filesLoading?.[videoThumbnailFileName] || editVideoThumbnailLoading}
                rowId={rowId}
                src={activeThumbnail?.downloadUri}
                deleteTooltip={isCustom ? t("videos.deleteThumbnail") : t("videos.onlyCustomThumbnailDeletable")}
                onFileSelect={uploadThumbnailHandler}
                onDelete={() =>
                    setModalData({
                        action: deleteCustomThumbnailHandler,
                        actionType: actionsData.delete,
                        body: t("messages.videoMsgs.deleteCustomThumbnailAlert"),
                        open: true,
                        title: t("videos.deleteCustomThumbnail"),
                    })
                }
            />
            <Modal modalData={modalData} setModalData={setModalData} />
        </>
    );
};

export default ReleaseVideoThumbnail;

ReleaseVideoThumbnail.propTypes = {
    image: PropTypes.object,
    imageId: PropTypes.number,
    rowId: PropTypes.number,
    thumbnail: PropTypes.object,
    thumbnailId: PropTypes.number,
    trackId: PropTypes.number,
    videoId: PropTypes.number,
};
